// src/Pages/MDAReview.js
import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import UserProfileHeader from "./UserProfileHeader";

export default function MDAReview() {
  const [sp] = useSearchParams();
  const token = sp.get("token");
  const preAction = sp.get("action"); // "approve" | "reject" | null
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);

  const API = useMemo(
    () => process.env.REACT_APP_API || process.env.REACT_APP_BACKEND_URL || "",
    []
  );

  const operatorEmail = useMemo(
    () => (localStorage.getItem("userEmail") || "").trim(),
    []
  );

  const isPending = useMemo(
    () => /^pending/i.test(msg?.status || ""),
    [msg?.status]
  );

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios
      .post(`${API}/get_message_by_token`, { token })
      .then(({ data }) => {
        if (data?.success) setMsg(data.data);
        else toast.error(data?.msg || "Message not found");
      })
      .catch(() => toast.error("Failed to load message"))
      .finally(() => setLoading(false));
  }, [API, token]);

  const refresh = useCallback(() => {
    if (!token) return Promise.resolve();
    return axios
      .post(`${API}/get_message_by_token`, { token })
      .then(({ data }) => data?.success && setMsg(data.data))
      .catch(() => {});
  }, [API, token]);

  const act = useCallback(
    (action) => {
      if (!token) return;
      if (!operatorEmail) {
        toast.error("Please log in first, then try again.");
        return;
      }

      const url = action === "approve" ? "/mda_approve" : "/mda_reject";
      setActing(true);

      axios
        .post(`${API}${url}`, { token, operator_email: operatorEmail })
        .then(async ({ data }) => {
          if (data?.success) {
            toast.success(action === "approve" ? "Approved." : "Rejected.");
            await refresh();
          } else {
            toast.error(data?.msg || "Action failed");
          }
        })
        .catch(() => toast.error("Network error"))
        .finally(() => setActing(false));
    },
    [API, token, operatorEmail, refresh]
  );

  // Auto-perform approve/reject when coming from email deep link
  useEffect(() => {
    if (!loading && msg && preAction && isPending) {
      if (!operatorEmail) {
        // The page can still load, but action needs a logged-in user.
        // (User might be auto-logged-in by existing session; if so, operatorEmail should exist.)
        toast.info("Log in to approve/reject this request.");
        return;
      }
      act(preAction);
    }
  }, [loading, msg, preAction, isPending, operatorEmail, act]);

  if (!token) return <div>Invalid link.</div>;
  if (loading) return <div>Loading…</div>;
  if (!msg) return <div>Request not found.</div>;

  const stageLabel =
    String(msg.status).includes("L2") ? "Approve (L2)" : "Approve (L1)";

  return (
    <div className="dashboard">
      {/* Pass isAdmin so the user icon menu renders all items */}
      <UserProfileHeader isAdmin={true} />

      <div className="container" style={{ padding: 16 }}>
        {(msg.type == "1" || msg.type == "2") && (<h2>Master Distriutor Registration Request</h2>)}
        {(msg.type == "3") && (<h2>Master Distributor Request VELOX CLOUD Key</h2>)}
        <p>
          <strong>Status:</strong> {msg.status}
        </p>
        {!operatorEmail && (
          <p style={{ color: "#b45309" }}>
            You are not logged in. Please log in before approving/rejecting.
          </p>
        )}
        <ul>
          <li>
            <strong>Name:</strong> {msg.first_name} {msg.last_name}
          </li>
          <li>
            <strong>Company:</strong> {msg.company_name}
          </li>
          <li>
            <strong>Email:</strong> {msg.email}
          </li>
          <li>
            <strong>Phone:</strong> {msg.phone || "-"}
          </li>
          <li>
            <strong>Quantity of Keys:</strong> {msg.order_keys_quantity || "-"}
          </li>
          <li>
            <strong>Location:</strong>{" "}
            {[msg.address, msg.city, msg.state, msg.country, msg.postal_code].filter(Boolean).join(", ") || "-"}
          </li>
          <li>
            <strong>Submitted:</strong>{" "}
            {msg.create ? new Date(msg.create).toLocaleString() : "-"}
          </li>
        </ul>

        {isPending && (
          <div style={{ display: "flex", gap: 12 }}>
            <button disabled={acting} onClick={() => act("approve")}>
              {stageLabel}
            </button>
            <button disabled={acting} onClick={() => act("reject")}>Reject</button>
          </div>
        )}
      </div>
    </div>
  );
}
